import React, { useState, useEffect } from 'react';
import { Linkedin, CheckCircle, AlertCircle, Loader2, Copy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { supabase } from '../../../lib/supabaseClient';

const DEFAULT_INDIVIDUAL_TEMPLATE = `🎯 New Opportunity Available

{{job_title}} | Ref: {{ref_code}}

{{description}}

📍 Location: {{locations}}
🔒 Clearance: {{clearance}}
💼 Work Arrangement: {{work_arrangement}}
📅 Start Date: {{start_date}}
⏳ Initial Term: {{term}}

Apply now: {application_url}

#hiring #opportunity #contract`;

const DEFAULT_MULTI_TEMPLATE = `🎯 New Opportunities Available

Role 1: {{job_title.1}} | Ref: {{ref_code.1}}

{{description.1}}

---

Role 2: {{job_title.2}} | Ref: {{ref_code.2}}

{{description.2}}

---

Explore all open roles and apply at: https://appsytec.com/jobs

#hiring #opportunities #contract`;

// Individual role placeholders — label, variable, description
const INDIVIDUAL_PLACEHOLDERS = [
  { variable: '{{job_title}}',        label: 'Job title' },
  { variable: '{{ref_code}}',         label: 'Reference code' },
  { variable: '{{client_name}}',      label: 'Client name' },
  { variable: '{{clearance}}',        label: 'Security clearance' },
  { variable: '{{locations}}',        label: 'Locations' },
  { variable: '{{work_arrangement}}', label: 'Work arrangement' },
  { variable: '{{close_date}}',       label: 'Closing date' },
  { variable: '{{start_date}}',       label: 'Start date' },
  { variable: '{{term}}',             label: 'Initial term' },
  { variable: '{{extension}}',        label: 'Extension options' },
  { variable: '{{description}}',      label: 'Position description' },
  { variable: '{{criteria}}',         label: 'Selection criteria' },
  { variable: '{application_url}',    label: 'Public apply URL' },
];

// Multi-role placeholders — same fields but with .1 / .2 / .3 suffix
const MULTI_PLACEHOLDER_FIELDS = [
  { suffix: 'job_title',        label: 'Job title' },
  { suffix: 'ref_code',         label: 'Reference code' },
  { suffix: 'client_name',      label: 'Client name' },
  { suffix: 'clearance',        label: 'Security clearance' },
  { suffix: 'locations',        label: 'Locations' },
  { suffix: 'work_arrangement', label: 'Work arrangement' },
  { suffix: 'close_date',       label: 'Closing date' },
  { suffix: 'start_date',       label: 'Start date' },
  { suffix: 'term',             label: 'Initial term' },
  { suffix: 'extension',        label: 'Extension options' },
  { suffix: 'description',      label: 'Position description' },
  { suffix: 'criteria',         label: 'Selection criteria' },
];

const PlaceholderChips = ({ placeholders, copied, onCopy }) => (
  <div className="flex flex-wrap gap-1.5">
    {placeholders.map(({ variable, label }) => (
      <button
        key={variable}
        onClick={() => onCopy(variable)}
        title={label}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded border bg-background hover:bg-muted transition-colors text-xs"
      >
        {copied === variable ? (
          <CheckCircle className="h-3 w-3 text-green-600 shrink-0" />
        ) : (
          <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        <span className="font-mono">{variable}</span>
        <span className="text-muted-foreground border-l pl-1.5">{label}</span>
      </button>
    ))}
  </div>
);

const LinkedInTemplates = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [copied, setCopied] = useState(null);
  const [activeRoleTab, setActiveRoleTab] = useState('1'); // for multi template variable preview

  const [individualTemplate, setIndividualTemplate] = useState(DEFAULT_INDIVIDUAL_TEMPLATE);
  const [multiTemplate, setMultiTemplate] = useState(DEFAULT_MULTI_TEMPLATE);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('linkedin_integrations')
        .select('post_template, multi_post_template')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching LinkedIn templates:', error);
      }

      if (data) {
        if (data.post_template) setIndividualTemplate(data.post_template);
        if (data.multi_post_template) setMultiTemplate(data.multi_post_template);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('linkedin_integrations')
        .upsert({
          user_id: user.id,
          post_template: individualTemplate,
          multi_post_template: multiTemplate,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Templates saved successfully' });
    } catch (error) {
      console.error('Error saving templates:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save templates' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (variable) => {
    navigator.clipboard.writeText(variable).then(() => {
      setCopied(variable);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  // Build multi placeholders for the selected role number
  const multiPlaceholders = MULTI_PLACEHOLDER_FIELDS.map(({ suffix, label }) => ({
    variable: `{{${suffix}.${activeRoleTab}}}`,
    label: `${label} (role ${activeRoleTab})`,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">LinkedIn Templates</h3>
        <p className="text-sm text-muted-foreground">
          Customise the post templates used when sharing roles on LinkedIn
        </p>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-destructive' : 'border-green-500'}>
          {message.type === 'error'
            ? <AlertCircle className="h-4 w-4" />
            : <CheckCircle className="h-4 w-4" />}
          <AlertDescription className={message.type === 'error' ? 'text-destructive' : 'text-green-700'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      {/* Individual Role Template */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-[#0077B5] rounded-lg flex items-center justify-center shrink-0">
              <Linkedin className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Individual Role Template</CardTitle>
              <CardDescription>Used when posting a single RFQ to LinkedIn</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Available Placeholders</Label>
            <p className="text-xs text-muted-foreground">Click a placeholder to copy it, then paste into the template below.</p>
            <PlaceholderChips
              placeholders={INDIVIDUAL_PLACEHOLDERS}
              copied={copied}
              onCopy={handleCopy}
            />
          </div>

          <Textarea
            value={individualTemplate}
            onChange={(e) => setIndividualTemplate(e.target.value)}
            placeholder="Enter your LinkedIn post template..."
            rows={16}
            className="font-mono text-sm"
          />

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setIndividualTemplate(DEFAULT_INDIVIDUAL_TEMPLATE)}>
              Reset to Default
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Multiple Role Template */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-[#0077B5] rounded-lg flex items-center justify-center shrink-0">
              <Linkedin className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Multiple Role Template</CardTitle>
              <CardDescription>Used when posting multiple RFQs together from the RFQs list</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Available Placeholders</Label>
            <p className="text-xs text-muted-foreground">
              Each placeholder is numbered by role position. Select a role number to copy its placeholders.
            </p>
            {/* Role number tabs */}
            <div className="flex gap-1">
              {['1', '2', '3'].map((n) => (
                <button
                  key={n}
                  onClick={() => setActiveRoleTab(n)}
                  className={`px-3 py-1 rounded text-xs font-medium border transition-colors ${
                    activeRoleTab === n
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-muted border-input'
                  }`}
                >
                  Role {n}
                </button>
              ))}
            </div>
            <PlaceholderChips
              placeholders={multiPlaceholders}
              copied={copied}
              onCopy={handleCopy}
            />
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              The <Badge variant="secondary" className="font-mono text-xs mx-0.5">.1</Badge>
              <Badge variant="secondary" className="font-mono text-xs mx-0.5">.2</Badge>
              <Badge variant="secondary" className="font-mono text-xs mx-0.5">.3</Badge> suffixes
              correspond to the 1st, 2nd, and 3rd selected roles on the RFQs page. Unused placeholders are removed automatically.
            </AlertDescription>
          </Alert>

          <Textarea
            value={multiTemplate}
            onChange={(e) => setMultiTemplate(e.target.value)}
            placeholder="Enter your multi-role LinkedIn post template..."
            rows={18}
            className="font-mono text-sm"
          />

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setMultiTemplate(DEFAULT_MULTI_TEMPLATE)}>
              Reset to Default
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Templates'
          )}
        </Button>
      </div>
    </div>
  );
};

export default LinkedInTemplates;
